import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/EngineGlassmorphism.css';

const API_URL = import.meta.env?.VITE_API_URL || 'https://tube.contentserp.in/api/engine';

export default function AnalysisHistory({ user }) {
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Trend State (Phase 1.4)
  const [activeTrendTarget, setActiveTrendTarget] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [filter]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const url = filter ? `${API_URL}/history?status=${filter}` : `${API_URL}/history`;
      const res = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
    } catch (e) {
      console.error('Failed to load history', e);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchTrend = async (targetId) => {
    setActiveTrendTarget(targetId);
    setTrendLoading(true);
    setTrendData(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}/history/trends/${encodeURIComponent(targetId)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data.success) {
        setTrendData(res.data.trends || []);
      }
    } catch (e) {
      console.error('Failed to fetch trend data:', e);
    } finally {
      setTrendLoading(false);
    }
  };

  const handleOpenJob = (jobId, status) => {
    if (status === 'completed') {
       navigate(`/video-audit?jobId=${jobId}`);
    } else {
       alert('Only completed jobs can be viewed.');
    }
  };

  return (
    <div className="engine-dashboard">
      <header className="engine-header">
        <h1>🕒 Analysis History & Score Trends</h1>
        <p>View your past AI Video Audits and historical score progression graphs.</p>
      </header>

      <div className="engine-glass-panel" style={{marginBottom: '2rem'}}>
        <select 
           className="engine-input" 
           value={filter} 
           onChange={e => setFilter(e.target.value)}
           style={{maxWidth: '300px'}}
        >
          <option value="">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="processing">Processing</option>
        </select>
      </div>

      {/* Historical Score Trend Panel (Phase 1.4) */}
      {activeTrendTarget && (
        <div className="engine-glass-panel" style={{ marginBottom: '2rem', border: '1px solid #8b5cf6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#f8fafc' }}>
              📈 Score Progression Trend for Target: <strong style={{ color: '#c084fc' }}>{activeTrendTarget}</strong>
            </h3>
            <button 
              className="engine-btn" 
              style={{ background: '#475569', padding: '0.3rem 0.8rem', fontSize: '0.8rem' }}
              onClick={() => { setActiveTrendTarget(null); setTrendData(null); }}
            >
              Close Graph
            </button>
          </div>

          {trendLoading ? (
            <p style={{ color: '#94a3b8' }}>⏳ Loading historical score trend graph...</p>
          ) : trendData && trendData.length > 0 ? (
            <div>
              {/* Visual Score Bars over Time */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                {trendData.map((item, idx) => (
                  <div key={idx} style={{ background: 'rgba(0,0,0,0.3)', padding: '1rem', borderRadius: '10px', borderLeft: '4px solid #8b5cf6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <span style={{ color: '#cbd5e1', fontWeight: 600 }}>Audit #{idx + 1} — {new Date(item.createdAt).toLocaleString()}</span>
                      <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '1.1rem' }}>Overall Score: {item.overallScore}/100</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.8rem', marginTop: '0.5rem' }}>
                      <div style={{ background: 'rgba(255,255,255,0.03)', padding: '0.5rem 0.8rem', borderRadius: '6px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>SEO Score</span>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#34d399' }}>{item.seoScore}</div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.03)', padding: '0.5rem 0.8rem', borderRadius: '6px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Engagement</span>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#f472b6' }}>{item.engagementScore}</div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.03)', padding: '0.5rem 0.8rem', borderRadius: '6px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Quality</span>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#c084fc' }}>{item.contentQualityScore}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ color: '#94a3b8' }}>No trend data history found for this target.</p>
          )}
        </div>
      )}

      <div className="engine-glass-panel">
        {loading ? (
          <p>Loading history...</p>
        ) : history.length === 0 ? (
          <p>No jobs found.</p>
        ) : (
          <table style={{width: '100%', textAlign: 'left', borderCollapse: 'collapse'}}>
            <thead>
              <tr style={{borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
                <th style={{padding: '1rem'}}>Target ID</th>
                <th style={{padding: '1rem'}}>Status</th>
                <th style={{padding: '1rem'}}>Created At</th>
                <th style={{padding: '1rem'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map(job => (
                <tr key={job.id} style={{borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                  <td style={{padding: '1rem', fontWeight: 600, color: '#f8fafc'}}>{job.target_id}</td>
                  <td style={{padding: '1rem'}}>
                    <span className="ai-badge" style={{background: job.status==='completed' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)'}}>
                      {job.status}
                    </span>
                  </td>
                  <td style={{padding: '1rem'}}>{new Date(job.created_at).toLocaleString()}</td>
                  <td style={{padding: '1rem', display: 'flex', gap: '0.5rem'}}>
                    <button 
                      className="engine-btn" 
                      style={{padding: '0.4rem 0.8rem', fontSize: '0.85rem'}}
                      onClick={() => handleOpenJob(job.id, job.status)}
                      disabled={job.status !== 'completed'}
                    >
                      View Report
                    </button>
                    {job.status === 'completed' && (
                      <button 
                        className="engine-btn" 
                        style={{padding: '0.4rem 0.8rem', fontSize: '0.85rem', background: '#8b5cf6'}}
                        onClick={() => handleFetchTrend(job.target_id)}
                      >
                        📈 Score Trend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
